import type {
  EquipmentIntake,
  IntakeAccessoryInput,
  IntakeOwnershipType,
  IntakeShipment,
  ReceivedCondition,
  UpsertShipmentPayload,
} from '@/services/equipment-intake.service';
import type { AccessoryRow } from './components/AccessoryChecklist';
import type { ShipmentState } from './components/ShipmentCard';
import type { ReceivedConditionState } from './components/ReceivedConditionChecklist';

/**
 * One description of what an intake form holds, and one set of rules for
 * turning it into requests.
 *
 * The intake page and the Ops workspace each carried their own copy of this
 * shape, its defaults and its validation. Two copies of a validation rule is
 * one rule and one latent bug — this module exists so there is only ever one.
 *
 * WHAT IS DELIBERATELY NOT HERE:
 *   - draft persistence. That is a create-only concern and stays in the
 *     workspace, which owns the localStorage contract.
 *   - anything server-side. These are pure functions over form values; every
 *     request still goes through the existing services, so tenant resolution
 *     and RLS are untouched.
 */

/** Fields that may only ever be set at creation. Named once, so the edit path
 * and any future caller agree on what "create-only" means without restating a
 * list that could drift. */
export const CREATE_ONLY_FIELDS = [
  'customerId',
  'equipmentId',
  'intakeDate',
  'ownershipTypeAtIntake',
] as const;

/** What the form collects. Create and edit submit the same shape; the write
 * paths differ, not the values. */
export interface IntakeFormData {
  intakeDate: string;
  customerId: string;
  customerLocationId: string;
  customerContactId: string;
  equipmentId: string;
  ownershipTypeAtIntake: IntakeOwnershipType | '';
  complaintDescription: string;
  /** Authoritative condition record. `conditionOnReceipt` is legacy and is
   * never written by this form. */
  receivedConditionJson: ReceivedCondition;
  remarks: string;
  accessories: IntakeAccessoryInput[];
  /** Inspection-owned. Carried here for convenience; written through the
   * inspection endpoint, never on the intake payload. */
  assignedEngineerId: string;
  /** Inspection-owned. Same rule. */
  priorityId: string;
  shipments: UpsertShipmentPayload[];
}

/** What an existing intake seeds the form with. */
export interface IntakeFormInitial {
  intakeId: string;
  intakeDate: string;
  customerId: string | null;
  customerLocationId: string | null;
  customerContactId: string | null;
  equipmentId: string;
  equipmentLabel: string;
  /** Display label for the locked customer field. The customer is CREATE-ONLY,
   * so edit renders this text rather than a picker. */
  customerLabel: string;
  ownershipTypeAtIntake: IntakeOwnershipType;
  complaintDescription: string;
  /** Legacy free text — displayed read-only, never written back. */
  conditionOnReceipt: string | null;
  receivedConditionJson: ReceivedCondition | null;
  remarks: string | null;
  accessories: IntakeAccessoryInput[];
  photoUrls: string[];
  shipments: IntakeShipment[];
  /** Legacy compatibility copy on the intake row. The inspection is the source
   * of truth; this is shown only until the inspection query resolves. */
  legacyAssignedEngineerId: string | null;
  currentShipToLabel: string;
}

export interface IntakeFileState {
  photoPendingFiles: File[];
  photoRemovedIndices: number[];
  shipmentStates: ShipmentState[];
}

export type IntakeFormMode = 'create' | 'edit' | 'readonly';

/** Behaviour per mode. Layout never varies — only what may be changed and
 * where it is written. */
export function modeFlags(mode: IntakeFormMode) {
  return {
    isCreate: mode === 'create',
    isEdit: mode === 'edit',
    isReadonly: mode === 'readonly',
    /** Create-only fields accept input only while creating. */
    canEditCreateOnly: mode === 'create',
    /** Everything else is writable unless the whole form is read-only. */
    canEdit: mode !== 'readonly',
  };
}

/** Builds the initial seed from a fetched intake. One mapping, so the edit
 * screen and any future read-only embed agree. */
export function initialFromIntake(intake: EquipmentIntake): IntakeFormInitial {
  return {
    intakeId: intake.id,
    intakeDate: intake.intakeDate,
    customerId: intake.customerId,
    customerLocationId: intake.customerLocationId,
    customerContactId: intake.customerContactId,
    equipmentId: intake.equipmentId,
    equipmentLabel: `${intake.equipmentName} | ${intake.equipmentSerialNumber} | ${intake.equipmentAssetNumber}`,
    // Legal name first, matching the list and the preview. Company-owned
    // equipment has no customer at all.
    customerLabel: [
      intake.customerOrganizationName ?? intake.customerName ?? 'Company equipment',
      intake.customerCode,
    ]
      .filter(Boolean)
      .join(' | '),
    ownershipTypeAtIntake: intake.ownershipTypeAtIntake,
    complaintDescription: intake.complaintDescription,
    conditionOnReceipt: intake.conditionOnReceipt,
    receivedConditionJson: intake.receivedConditionJson,
    remarks: intake.remarks,
    accessories: intake.accessories.map((a) => ({
      accessoryId: a.accessoryId ?? undefined,
      accessoryName: a.accessoryName,
      status: a.status,
      notes: a.notes ?? undefined,
    })),
    photoUrls: intake.photoUrls,
    shipments: intake.shipments,
    legacyAssignedEngineerId: intake.assignedEngineerId,
    // Shown by the Ship-To suggestion so the operator can compare the frozen
    // snapshot against the location they just picked. A caller without
    // intake:billing:view receives no snapshot at all, hence the dash.
    currentShipToLabel: intake.shipToAddress
      ? [intake.shipToAddress.label, intake.shipToAddress.city].filter(Boolean).join(' — ')
      : '—',
  };
}

/** Accessory rows → the API's accessory input. Blank names are dropped, never
 * sent as empty strings. */
export function toAccessoryInputs(rows: AccessoryRow[]): IntakeAccessoryInput[] {
  return rows
    .filter((r) => r.accessoryName.trim())
    .map((r) => ({
      accessoryId: r.accessoryId ?? undefined,
      accessoryName: r.accessoryName.trim(),
      status: r.status,
      notes: r.notes.trim() || undefined,
    }));
}

/** Shipment card state → the API's upsert payload. */
export function toShipmentPayloads(states: ShipmentState[]): UpsertShipmentPayload[] {
  return states.map((s) => ({
    id: s.id,
    shipmentType: s.shipmentType,
    courierName: s.courierName.trim() || undefined,
    trackingNumber: s.trackingNumber.trim() || undefined,
    dispatchDate: s.dispatchDate ? new Date(s.dispatchDate).toISOString() : undefined,
    receivedDate: s.receivedDate ? new Date(s.receivedDate).toISOString() : undefined,
    remarks: s.remarks.trim() || undefined,
  }));
}

export interface IntakeValidationInput {
  equipmentId: string;
  complaintDescription: string;
  customerLocationId: string;
  customerContactId: string;
  /** A customer created inline during this intake must be given a location and
   * a contact before the intake can be saved. */
  isNewCustomer: boolean;
}

/**
 * The single validation rule set. Returns the first blocking message, or null.
 *
 * Both screens previously implemented this inline and only one of them enforced
 * the new-customer rule consistently.
 */
export function validateIntakeForm(input: IntakeValidationInput): string | null {
  if (!input.equipmentId) return 'Select the equipment being received.';
  if (!input.complaintDescription.trim()) return 'A complaint description is required.';
  if (input.isNewCustomer && (!input.customerLocationId || !input.customerContactId)) {
    return 'A newly created customer needs a location and a contact before intake.';
  }
  return null;
}

/**
 * The condition checklist the form should start from.
 *
 * `receivedConditionJson` is authoritative. An intake that never recorded one —
 * every bulk-created intake, and anything predating the feature — starts with
 * all six items present and unset so it can be completed here. The legacy
 * `conditionOnReceipt` free text is never parsed into this: it is prose, and
 * guessing structure from prose would fabricate observations nobody made.
 */
export function conditionSeed(
  initial: Pick<IntakeFormInitial, 'receivedConditionJson'> | undefined,
  fromReceivedCondition: (v: unknown) => ReceivedConditionState,
): ReceivedConditionState {
  return fromReceivedCondition(initial?.receivedConditionJson);
}
